/**
 * Camera helpers — secure-context checks + getUserMedia with legacy fallbacks.
 */

export function isSecureCameraContext() {
  if (typeof window === "undefined") {
    return true;
  }

  if (window.isSecureContext) {
    return true;
  }

  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
}

export function getCameraOriginHint() {
  if (typeof window === "undefined") {
    return "";
  }
  return `${window.location.protocol}//${window.location.host}`;
}

/**
 * Resolve getUserMedia across modern + legacy WebKit/Android browsers.
 */
export function getGetUserMedia() {
  if (typeof navigator === "undefined") {
    return null;
  }

  if (navigator.mediaDevices?.getUserMedia) {
    return (constraints) => navigator.mediaDevices.getUserMedia(constraints);
  }

  const legacy =
    navigator.getUserMedia ||
    navigator.webkitGetUserMedia ||
    navigator.mozGetUserMedia ||
    navigator.msGetUserMedia;

  if (legacy) {
    return (constraints) =>
      new Promise((resolve, reject) => {
        try {
          legacy.call(navigator, constraints, resolve, reject);
        } catch (err) {
          reject(err);
        }
      });
  }

  try {
    if (navigator.mediaDevices && typeof navigator.mediaDevices === "object") {
      const bound = navigator.mediaDevices.getUserMedia?.bind(
        navigator.mediaDevices
      );
      if (bound) {
        return bound;
      }
    }
  } catch {
    /* ignore */
  }

  return null;
}

export async function openCameraStream() {
  const getUserMedia = getGetUserMedia();
  if (!getUserMedia) {
    const origin = getCameraOriginHint();
    const secure = isSecureCameraContext();
    throw Object.assign(
      new Error(
        secure
          ? "Camera API is blocked in this browser. Allow camera permission or use Chrome."
          : `Camera needs HTTPS. You are on ${origin || "an insecure page"}. Open the site with https:// and allow camera access.`
      ),
      { code: "CAMERA_API_UNAVAILABLE", insecure: !secure }
    );
  }

  const attempts = [
    {
      audio: false,
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    },
    {
      audio: false,
      video: { facingMode: { ideal: "environment" } },
    },
    {
      audio: false,
      video: { facingMode: "environment" },
    },
    {
      audio: false,
      video: true,
    },
  ];

  let lastError;
  for (const constraints of attempts) {
    try {
      return await getUserMedia(constraints);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Unable to open camera.");
}

/**
 * Read a File/Blob as a JPEG data URL for the existing submit pipeline.
 */
export function fileToJpegDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file selected."));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read photo."));
    reader.readAsDataURL(file);
  });
}

/**
 * Shrink a data-URL image so it can be stored / uploaded on mobile.
 * Times out and returns the original if decode hangs.
 */
export function compressImageDataUrl(
  dataUrl,
  maxWidth = 1280,
  quality = 0.72,
  timeoutMs = 8000
) {
  return new Promise((resolve) => {
    if (!dataUrl || typeof dataUrl !== "string") {
      resolve(dataUrl);
      return;
    }

    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timer = setTimeout(() => done(dataUrl), timeoutMs);
    const image = new Image();
    image.onload = () => {
      try {
        const scale = Math.min(1, maxWidth / (image.width || maxWidth));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round((image.width || maxWidth) * scale));
        canvas.height = Math.max(
          1,
          Math.round((image.height || maxWidth) * scale)
        );
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        clearTimeout(timer);
        done(canvas.toDataURL("image/jpeg", quality));
      } catch {
        clearTimeout(timer);
        done(dataUrl);
      }
    };
    image.onerror = () => {
      clearTimeout(timer);
      done(dataUrl);
    };
    image.src = dataUrl;
  });
}
